class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-cart-upsell-add]');
      if (!button) return;
      event.preventDefault();
      this.addUpsellProduct(button);
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.resetScrollPosition();
      requestAnimationFrame(() => this.resetScrollPosition());
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  resetScrollPosition() {
    const scrollRegion = this.querySelector('.lfc-cart__scroll-region');
    if (scrollRegion) scrollRegion.scrollTop = 0;
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerSection = this.getSectionsToRender().find((section) => section.selector === '#CartDrawer');
    const incomingDrawer = drawerSection && parsedState.sections?.[drawerSection.id]
      ? this.getSectionDOM(parsedState.sections[drawerSection.id], 'cart-drawer')
      : null;

    if (incomingDrawer) {
      this.classList.toggle('is-empty', incomingDrawer.classList.contains('is-empty'));
      this.classList.toggle('lfc-cart--upsell-rail', incomingDrawer.classList.contains('lfc-cart--upsell-rail'));
    } else {
      this.classList.remove('is-empty');
    }

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections?.[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    const sectionId = this.dataset.sectionId || 'cart-drawer';
    return [
      {
        id: sectionId,
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  async addUpsellProduct(button) {
    if (button.disabled) return;
    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const sections = this.getSectionsToRender().map((section) => section.id);
    const body = JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
      sections,
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(routes.cart_add_url, { ...fetchConfig(), body });
      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || window.cartStrings.error);
      this.renderContents(state);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-upsell', cartData: state, variantId });
    } catch (error) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.setAttribute('title', error.message || window.cartStrings.error);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sectionId = this.closest('cart-drawer')?.dataset.sectionId || 'cart-drawer';
    return [
      {
        id: 'CartDrawer',
        section: sectionId,
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
